const COUNTRY_PREFERENCE_KEY = "jobsrmine.country-code";
export const COUNTRY_PREFERENCE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredCountryPreference {
  countryCode: string;
  expiresAt: number;
}

/** Persists a stable ISO code for seven days; country details come from the API. */
export class CountryPreferenceService {
  constructor(
    private readonly storage: Storage | null = browserStorage(),
    private readonly now: () => number = Date.now,
  ) {}

  read(): string | null {
    if (!this.storage) return null;
    try {
      const rawPreference = this.storage.getItem(COUNTRY_PREFERENCE_KEY);
      if (!rawPreference) return null;

      const preference = JSON.parse(rawPreference) as Partial<StoredCountryPreference>;
      const code = preference.countryCode?.trim().toUpperCase();
      if (
        !code ||
        !/^[A-Z]{2}$/.test(code) ||
        typeof preference.expiresAt !== "number" ||
        !Number.isFinite(preference.expiresAt) ||
        preference.expiresAt <= this.now()
      ) {
        this.clear();
        return null;
      }
      return code;
    } catch {
      this.clear();
      return null;
    }
  }

  save(countryCode: string): void {
    if (!this.storage) return;
    const normalizedCode = countryCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(normalizedCode)) return;

    try {
      this.storage.setItem(
        COUNTRY_PREFERENCE_KEY,
        JSON.stringify({
          countryCode: normalizedCode,
          expiresAt: this.now() + COUNTRY_PREFERENCE_TTL_MS,
        } satisfies StoredCountryPreference),
      );
    } catch {
      // Storage can be unavailable in private browsing or restricted embeds.
    }
  }

  private clear(): void {
    try {
      this.storage?.removeItem(COUNTRY_PREFERENCE_KEY);
    } catch {
      // Storage can be unavailable in private browsing or restricted embeds.
    }
  }
}

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
